import { useState, useEffect } from "react";
import { useGoogleAuth } from "@/hooks/useGoogleAuth";
import { trpc } from "../lib/trpc";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { Plus, Search, Power, Pencil, Landmark } from "lucide-react";
import { Badge } from "../components/ui/badge";

const BANCOS_COMUNS = [
  { codigo: "001", nome: "BANCO DO BRASIL S.A." },
  { codigo: "033", nome: "BANCO SANTANDER (BRASIL) S.A." },
  { codigo: "104", nome: "CAIXA ECONOMICA FEDERAL" },
  { codigo: "237", nome: "BANCO BRADESCO S.A." },
  { codigo: "341", nome: "BANCO ITAU S.A." },
  { codigo: "422", nome: "BANCO SAFRA S.A." },
  { codigo: "748", nome: "BANCO COOPERATIVO SICREDI S.A." },
  { codigo: "756", nome: "BANCO COOPERATIVO DO BRASIL S.A. - BANCOOB (SICOOB)" },
  { codigo: "077", nome: "BANCO INTER S.A." },
  { codigo: "260", nome: "NU PAGAMENTOS S.A. (NUBANK)" },
  { codigo: "336", nome: "BANCO C6 S.A." },
  { codigo: "212", nome: "BANCO ORIGINAL S.A." },
  { codigo: "655", nome: "BANCO VOTORANTIM S.A." },
  { codigo: "041", nome: "BANCO DO ESTADO DO RIO GRANDE DO SUL S.A. (BANRISUL)" },
  { codigo: "070", nome: "BRB - BANCO DE BRASILIA S.A." },
];

const ESPECIES_TITULO = [
  { valor: "DM", label: "DM - Duplicata Mercantil" },
  { valor: "NP", label: "NP - Nota Promissória" },
  { valor: "DS", label: "DS - Duplicata de Serviço" },
  { valor: "RC", label: "RC - Recibo" },
  { valor: "LC", label: "LC - Letra de Câmbio" },
  { valor: "ND", label: "ND - Nota de Débito" },
  { valor: "CC", label: "CC - Cheque" },
  { valor: "CH", label: "CH - Cheque" },
  { valor: "BDP", label: "BDP - Boleto de Proposta" },
  { valor: "OUT", label: "OUT - Outros" },
];

const INSTRUCOES_COBRANCA = [
  { valor: "none", label: "Nenhuma" },
  { valor: "02", label: "02 - Devolver após N dias" },
  { valor: "06", label: "06 - Cobrar juros de mora" },
  { valor: "09", label: "09 - Protestar após N dias" },
  { valor: "19", label: "19 - Não protestar" },
  { valor: "23", label: "23 - Não cobrar juros de mora" },
];

const MODOS_PAGAMENTO = [
  "01-Dinheiro",
  "02-Cheque",
  "03-Cartão de Crédito",
  "04-Cartão de Débito",
  "05-Crédito Loja",
  "10-Vale Alimentação",
  "11-Vale Refeição",
  "12-Vale Presente",
  "13-Vale Combustível",
  "15-Boleto Bancário",
  "16-Depósito Bancário",
  "17-PIX",
  "18-Transferência Bancária / Carteira Digital",
  "19-Programa de Fidelidade / Cashback",
  "90-Sem Pagamento",
  "99-Outros",
];

const emptyForm = {
  emitenteId: 0,
  emitenteNome: "",
  modosPagamento: "",
  codigoBanco: "",
  nomeBanco: "",
  agencia: "",
  agenciaDac: "",
  conta: "",
  contaDac: "",
  nomeEmpresa: "",
  carteira: "",
  nossoNumeroInicial: 1,
  especieTitulo: "DM",
  instrucao1: "",
  instrucao2: "",
  diasProtesto: "",
  jurosAoDia: "",
  multaPercentual: "",
  descontoPrazo: "",
  descontoValor: "",
};

export default function BankAccounts() {
  const { user, loading: authLoading } = useGoogleAuth();
  const [searchTerm, setSearchTerm] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [isEmitenteModalOpen, setIsEmitenteModalOpen] = useState(false);
  const [formData, setFormData] = useState({ ...emptyForm });
  const [selectedModos, setSelectedModos] = useState<string[]>([]);

  useEffect(() => {
    if (!authLoading && !user) {
      window.location.href = "/login";
    }
  }, [user, authLoading]);

  const { data: contas = [], refetch } = trpc.bankAccounts.list.useQuery(
    user?.emitenteId ? { emitenteId: user.emitenteId } : undefined,
    { enabled: !!user }
  );

  const { data: emitentes = [] } = trpc.emitentes.list.useQuery(undefined, {
    enabled: !!user && user.role === "admin",
  });

  const createMutation = trpc.bankAccounts.create.useMutation({
    onSuccess: () => {
      toast.success("Conta bancária criada com sucesso");
      handleCloseDialog();
      refetch();
    },
    onError: (e) => toast.error(e.message || "Erro ao criar conta bancária"),
  });

  const updateMutation = trpc.bankAccounts.update.useMutation({
    onSuccess: () => {
      toast.success("Conta bancária atualizada com sucesso");
      handleCloseDialog();
      refetch();
    },
    onError: (e) => toast.error(e.message || "Erro ao atualizar conta bancária"),
  });

  const toggleMutation = trpc.bankAccounts.toggleAtivo.useMutation({
    onSuccess: () => {
      toast.success("Status atualizado com sucesso");
      refetch();
    },
    onError: (e) => toast.error(e.message || "Erro ao atualizar status"),
  });

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setEditingId(null);
    setFormData({ ...emptyForm });
    setSelectedModos([]);
  };

  const handleOpenCreate = () => {
    setFormData({
      ...emptyForm,
      emitenteId: user?.emitenteId ?? 0,
      emitenteNome: "",
    });
    setSelectedModos([]);
    setEditingId(null);
    setIsDialogOpen(true);
  };

  const handleEdit = (conta: any) => {
    setFormData({
      emitenteId: conta.emitenteId,
      emitenteNome: "",
      modosPagamento: conta.modosPagamento || "",
      codigoBanco: conta.codigoBanco,
      nomeBanco: conta.nomeBanco,
      agencia: conta.agencia,
      agenciaDac: conta.agenciaDac || "",
      conta: conta.conta,
      contaDac: conta.contaDac,
      nomeEmpresa: conta.nomeEmpresa,
      carteira: conta.carteira,
      nossoNumeroInicial: conta.nossoNumeroInicial ?? 1,
      especieTitulo: conta.especieTitulo || "DM",
      instrucao1: conta.instrucao1 || "",
      instrucao2: conta.instrucao2 || "",
      diasProtesto: conta.diasProtesto?.toString() || "",
      jurosAoDia: conta.jurosAoDia?.toString() || "",
      multaPercentual: conta.multaPercentual?.toString() || "",
      descontoPrazo: conta.descontoPrazo?.toString() || "",
      descontoValor: conta.descontoValor?.toString() || "",
    });
    setSelectedModos(conta.modosPagamento ? conta.modosPagamento.split(",").map((s: string) => s.trim()) : []);
    setEditingId(conta.id);
    setIsDialogOpen(true);
  };

  const handleBancoChange = (codigo: string) => {
    const banco = BANCOS_COMUNS.find((b) => b.codigo === codigo);
    setFormData((prev) => ({
      ...prev,
      codigoBanco: codigo,
      nomeBanco: banco?.nome || "",
    }));
  };

  const toggleModo = (modo: string) => {
    setSelectedModos((prev) =>
      prev.includes(modo) ? prev.filter((m) => m !== modo) : [...prev, modo]
    );
  };

  const handleSubmit = () => {
    if (!formData.emitenteId) return toast.error("Selecione o emitente");
    if (!formData.codigoBanco) return toast.error("Selecione o banco");
    if (!formData.agencia) return toast.error("Informe a agência");
    if (!formData.conta) return toast.error("Informe a conta");
    if (!formData.contaDac) return toast.error("Informe o dígito da conta");
    if (!formData.nomeEmpresa) return toast.error("Informe o nome da empresa");
    if (!formData.carteira) return toast.error("Informe a carteira");

    const payload = {
      emitenteId: formData.emitenteId,
      modosPagamento: selectedModos.length > 0 ? selectedModos.join(",") : null,
      codigoBanco: formData.codigoBanco,
      nomeBanco: formData.nomeBanco,
      agencia: formData.agencia,
      agenciaDac: formData.agenciaDac || null,
      conta: formData.conta,
      contaDac: formData.contaDac,
      nomeEmpresa: formData.nomeEmpresa.toUpperCase().slice(0, 30),
      carteira: formData.carteira,
      nossoNumeroInicial: Number(formData.nossoNumeroInicial) || 1,
      especieTitulo: formData.especieTitulo || "DM",
      instrucao1: formData.instrucao1 || null,
      instrucao2: formData.instrucao2 || null,
      diasProtesto: formData.diasProtesto ? Number(formData.diasProtesto) : null,
      jurosAoDia: formData.jurosAoDia ? Number(formData.jurosAoDia) : null,
      multaPercentual: formData.multaPercentual ? Number(formData.multaPercentual) : null,
      descontoPrazo: formData.descontoPrazo ? Number(formData.descontoPrazo) : null,
      descontoValor: formData.descontoValor ? Number(formData.descontoValor) : null,
    };

    if (editingId) {
      updateMutation.mutate({ id: editingId, ...payload });
    } else {
      createMutation.mutate(payload);
    }
  };

  const filtered = contas.filter((c: any) => {
    const term = searchTerm.toLowerCase();
    return (
      c.nomeBanco?.toLowerCase().includes(term) ||
      c.agencia?.includes(term) ||
      c.conta?.includes(term) ||
      c.nomeEmpresa?.toLowerCase().includes(term) ||
      c.codigoBanco?.includes(term)
    );
  });

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Landmark className="h-7 w-7 text-orange-500" />
          <div>
            <h1 className="text-2xl font-bold">Contas Bancárias</h1>
            <p className="text-sm text-muted-foreground">
              Gerencie as contas bancárias vinculadas aos emitentes
            </p>
          </div>
        </div>
        <Button onClick={handleOpenCreate} className="bg-orange-500 hover:bg-orange-600 text-white">
          <Plus className="h-4 w-4 mr-2" />
          Nova Conta
        </Button>
      </div>

      {/* Filtro */}
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Buscar por banco, agência, conta..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-9"
        />
      </div>

      {/* Lista */}
      <div className="grid gap-4">
        {filtered.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center text-muted-foreground">
              Nenhuma conta bancária encontrada.
            </CardContent>
          </Card>
        ) : (
          filtered.map((conta: any) => (
            <Card key={conta.id} className={conta.ativo ? "" : "opacity-60"}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div>
                      <p className="text-xs text-muted-foreground">Banco</p>
                      <p className="font-semibold text-sm">{conta.codigoBanco} — {conta.nomeBanco}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Agência / Conta</p>
                      <p className="font-semibold text-sm">
                        {conta.agencia}{conta.agenciaDac ? `-${conta.agenciaDac}` : ""} / {conta.conta}-{conta.contaDac}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Carteira</p>
                      <p className="font-semibold text-sm">{conta.carteira}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Empresa (CNAB)</p>
                      <p className="font-semibold text-sm">{conta.nomeEmpresa}</p>
                    </div>
                    {conta.modosPagamento && (
                      <div className="col-span-2 md:col-span-4">
                        <p className="text-xs text-muted-foreground mb-1">Modos de Pagamento</p>
                        <div className="flex flex-wrap gap-1">
                          {conta.modosPagamento.split(",").map((m: string) => (
                            <Badge key={m} variant="secondary" className="text-xs">{m.trim()}</Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleEdit(conta)}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant={conta.ativo ? "destructive" : "outline"}
                      onClick={() => toggleMutation.mutate({ id: conta.id })}
                      title={conta.ativo ? "Inativar" : "Ativar"}
                    >
                      <Power className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Modal */}
      <Dialog open={isDialogOpen} onOpenChange={(open) => !open && handleCloseDialog()}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingId ? "Editar Conta Bancária" : "Nova Conta Bancária"}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* Emitente — apenas admin vê */}
            {user?.role === "admin" && (
              <div className="space-y-1">
                <Label>Emitente *</Label>
                <Select
                  value={formData.emitenteId?.toString() || ""}
                  onValueChange={(v) => setFormData((p) => ({ ...p, emitenteId: Number(v) }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o emitente" />
                  </SelectTrigger>
                  <SelectContent>
                    {emitentes.map((e: any) => (
                      <SelectItem key={e.id} value={e.id.toString()}>
                        {e.razaoSocial}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Banco */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Banco *</Label>
                <Select value={formData.codigoBanco} onValueChange={handleBancoChange}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o banco" />
                  </SelectTrigger>
                  <SelectContent>
                    {BANCOS_COMUNS.map((b) => (
                      <SelectItem key={b.codigo} value={b.codigo}>
                        {b.codigo} — {b.nome}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Nome do Banco (CNAB)</Label>
                <Input
                  value={formData.nomeBanco}
                  onChange={(e) => setFormData((p) => ({ ...p, nomeBanco: e.target.value }))}
                  maxLength={15}
                />
              </div>
            </div>

            {/* Agência e Conta */}
            <div className="grid grid-cols-4 gap-3">
              <div className="col-span-2 space-y-1">
                <Label>Agência *</Label>
                <Input
                  value={formData.agencia}
                  onChange={(e) => setFormData((p) => ({ ...p, agencia: e.target.value.replace(/\D/g, "").slice(0, 4) }))}
                  placeholder="0000"
                  maxLength={4}
                />
              </div>
              <div className="space-y-1">
                <Label>Dígito Ag.</Label>
                <Input
                  value={formData.agenciaDac}
                  onChange={(e) => setFormData((p) => ({ ...p, agenciaDac: e.target.value.slice(0, 1) }))}
                  placeholder="X"
                  maxLength={1}
                />
              </div>
              <div className="space-y-1">
                <Label>Conta *</Label>
                <Input
                  value={formData.conta}
                  onChange={(e) => setFormData((p) => ({ ...p, conta: e.target.value.replace(/\D/g, "").slice(0, 5) }))}
                  placeholder="00000"
                  maxLength={5}
                />
              </div>
            </div>

            <div className="grid grid-cols-4 gap-3">
              <div className="space-y-1">
                <Label>Dígito Conta *</Label>
                <Input
                  value={formData.contaDac}
                  onChange={(e) => setFormData((p) => ({ ...p, contaDac: e.target.value.slice(0, 1) }))}
                  placeholder="X"
                  maxLength={1}
                />
              </div>
              <div className="col-span-3 space-y-1">
                <Label>Nome da Empresa (header CNAB, máx 30 chars) *</Label>
                <Input
                  value={formData.nomeEmpresa}
                  onChange={(e) => setFormData((p) => ({ ...p, nomeEmpresa: e.target.value.toUpperCase().slice(0, 30) }))}
                  placeholder="EMPRESA LTDA"
                  maxLength={30}
                />
              </div>
            </div>

            {/* Carteira e Espécie */}
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1">
                <Label>Carteira *</Label>
                <Input
                  value={formData.carteira}
                  onChange={(e) => setFormData((p) => ({ ...p, carteira: e.target.value.replace(/\D/g, "").slice(0, 3) }))}
                  placeholder="109"
                  maxLength={3}
                />
              </div>
              <div className="space-y-1">
                <Label>Espécie do Título</Label>
                <Select
                  value={formData.especieTitulo}
                  onValueChange={(v) => setFormData((p) => ({ ...p, especieTitulo: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ESPECIES_TITULO.map((e) => (
                      <SelectItem key={e.valor} value={e.valor}>{e.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Nosso Número Inicial</Label>
                <Input
                  type="number"
                  value={formData.nossoNumeroInicial}
                  onChange={(e) => setFormData((p) => ({ ...p, nossoNumeroInicial: Number(e.target.value) }))}
                  min={1}
                />
              </div>
            </div>

            {/* Instruções */}
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1">
                <Label>Instrução 1</Label>
                <Select
                  value={formData.instrucao1 || "none"}
                  onValueChange={(v) => setFormData((p) => ({ ...p, instrucao1: v === "none" ? null : v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Nenhuma" />
                  </SelectTrigger>
                  <SelectContent>
                    {INSTRUCOES_COBRANCA.map((i) => (
                      <SelectItem key={i.valor} value={i.valor}>{i.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Instrução 2</Label>
                <Select
                  value={formData.instrucao2 || "none"}
                  onValueChange={(v) => setFormData((p) => ({ ...p, instrucao2: v === "none" ? null : v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Nenhuma" />
                  </SelectTrigger>
                  <SelectContent>
                    {INSTRUCOES_COBRANCA.map((i) => (
                      <SelectItem key={i.valor} value={i.valor}>{i.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Dias para Protesto/Devolução</Label>
                <Input
                  type="number"
                  value={formData.diasProtesto}
                  onChange={(e) => setFormData((p) => ({ ...p, diasProtesto: e.target.value }))}
                  placeholder="0"
                />
              </div>
            </div>

            {/* Juros, Multa, Desconto */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="space-y-1">
                <Label>Juros ao Dia (R$)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.jurosAoDia}
                  onChange={(e) => setFormData((p) => ({ ...p, jurosAoDia: e.target.value }))}
                  placeholder="0.00"
                />
              </div>
              <div className="space-y-1">
                <Label>Multa (%)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.multaPercentual}
                  onChange={(e) => setFormData((p) => ({ ...p, multaPercentual: e.target.value }))}
                  placeholder="0.00"
                />
              </div>
              <div className="space-y-1">
                <Label>Desconto até (dias)</Label>
                <Input
                  type="number"
                  value={formData.descontoPrazo}
                  onChange={(e) => setFormData((p) => ({ ...p, descontoPrazo: e.target.value }))}
                  placeholder="0"
                />
              </div>
              <div className="space-y-1">
                <Label>Valor do Desconto (R$)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.descontoValor}
                  onChange={(e) => setFormData((p) => ({ ...p, descontoValor: e.target.value }))}
                  placeholder="0.00"
                />
              </div>
            </div>

            {/* Modos de Pagamento */}
            <div className="space-y-2">
              <Label>Modos de Pagamento vinculados</Label>
              <div className="flex flex-wrap gap-2">
                {MODOS_PAGAMENTO.map((modo) => (
                  <button
                    key={modo}
                    type="button"
                    onClick={() => toggleModo(modo)}
                    className={`px-3 py-1 rounded-full text-xs border transition-colors ${
                      selectedModos.includes(modo)
                        ? "bg-orange-500 text-white border-orange-500"
                        : "bg-white text-gray-700 border-gray-300 hover:border-orange-400"
                    }`}
                  >
                    {modo}
                  </button>
                ))}
              </div>
            </div>

            {/* Botões */}
            <div className="flex justify-end gap-3 pt-2">
              <Button variant="outline" onClick={handleCloseDialog}>Cancelar</Button>
              <Button
                onClick={handleSubmit}
                disabled={createMutation.isPending || updateMutation.isPending}
                className="bg-orange-500 hover:bg-orange-600 text-white"
              >
                {editingId ? "Salvar Alterações" : "Criar Conta"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
